import { debounce } from 'lodash';
import { useRef, useMemo, useEffect, useCallback, SyntheticEvent } from 'react';

type SearchVariables = Record<string, any>;
type RefetchFunction = (options?: { variables?: SearchVariables }) => void;

interface UseSearchWithDebounceProps {
  refetch: RefetchFunction;
  searchVariable: string;
  initialVariables?: SearchVariables;
  debounceDelay?: number;
}

interface UseSearchWithDebounceReturn {
  handleSearch: (event: SyntheticEvent<Element, Event>, searchValue: string) => void;
}

export function useSearchWithDebounce({
  refetch,
  searchVariable,
  initialVariables = {},
  debounceDelay = 1000,
}: UseSearchWithDebounceProps): UseSearchWithDebounceReturn {
  
  
  const initialVariablesRef = useRef(initialVariables);
  const hasInitialFetchRef = useRef(false);
  
  const debouncedSearch = useMemo(
    () => debounce((searchValue: string) => {
      const variables = {
        ...initialVariablesRef.current,
        [searchVariable]: searchValue,
      };
      
      refetch({ variables });
    }, debounceDelay),
    [refetch, searchVariable, debounceDelay]
  );

  const handleSearch = useCallback(
    (_: SyntheticEvent<Element, Event>, searchValue: string) => {
      debouncedSearch(searchValue);
    },
    [debouncedSearch]
  );

 
  useEffect(() => {
    if (!hasInitialFetchRef.current) {
      refetch({ variables: initialVariablesRef.current });
      hasInitialFetchRef.current = true;
    }
  }, [refetch]);

  return {
    handleSearch,
  };
}
